import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { clientsApi } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Users, MagnifyingGlass, Plus, WarningOctagon, Buildings, User as UserIcon, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import HelpButton from "@/components/HelpButton";

export default function Clients() {
    const [clients, setClients] = useState([]);
    const [q, setQ] = useState("");
    const [archived, setArchived] = useState(false);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const arr = await clientsApi.list(q || undefined, archived);
            setClients(arr);
        } catch {
            toast.error("Failed to load clients");
        }
        setLoading(false);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [archived]);

    useEffect(() => {
        const t = setTimeout(load, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q]);

    return (
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="clients-page">
            <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                <div className="rule-line pt-3">
                    <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-1">CRM</div>
                <div className="flex items-center gap-2">
                    <h1 className="font-serif text-4xl tracking-tight">Clients</h1>
                    <HelpButton topic="clients" testid="help-clients" />
                </div>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="new-client-btn"><Plus size={14} /> New client</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle className="font-serif text-2xl">New client intake</DialogTitle>
                            <DialogDescription>
                                Conflict check runs automatically against existing clients and opposing parties before save.
                            </DialogDescription>
                        </DialogHeader>
                        <IntakeForm
                            onDone={(c) => {
                                setOpen(false);
                                setClients((arr) => [c, ...arr]);
                                toast.success(`Client ${c.client_id_human} created`);
                            }}
                        />
                    </DialogContent>
                </Dialog>
            </div>

            <div className="flex items-center gap-3 mb-4 flex-wrap">
                <div className="relative flex-1 min-w-[220px] max-w-md">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Search by name, phone, email, or Client ID…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="pl-9"
                        data-testid="clients-search"
                    />
                </div>
                <Select value={archived ? "archived" : "active"} onValueChange={(v) => setArchived(v === "archived")}>
                    <SelectTrigger className="w-[180px]" data-testid="clients-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground">Loading…</div>
            ) : !clients.length ? (
                <div className="border border-dashed border-border rounded-md p-10 text-center" data-testid="clients-empty">
                    <Users size={28} className="mx-auto text-muted-foreground mb-3" weight="duotone" />
                    <div className="font-serif text-xl mb-1">No clients yet</div>
                    <div className="text-sm text-muted-foreground">Click "New client" to capture your first intake.</div>
                </div>
            ) : (
                <div className="border border-border rounded-md overflow-hidden bg-white">
                    <table className="w-full text-sm">
                        <thead className="bg-muted text-muted-foreground">
                            <tr>
                                <th className="text-left p-3 font-semibold">Client ID</th>
                                <th className="text-left p-3 font-semibold">Name</th>
                                <th className="text-left p-3 font-semibold hidden md:table-cell">Type</th>
                                <th className="text-left p-3 font-semibold hidden lg:table-cell">Contact</th>
                                <th className="text-left p-3 font-semibold hidden lg:table-cell">Matter type</th>
                                <th className="text-right p-3 font-semibold">Matters</th>
                                <th className="w-[40px]"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {clients.map((c) => (
                                <tr key={c.id} className="border-t border-border hover:bg-muted/40" data-testid={`client-row-${c.id}`}>
                                    <td className="p-3 font-mono text-xs">{c.client_id_human}</td>
                                    <td className="p-3">
                                        <Link to={`/clients/${c.id}`} className="font-medium hover:underline">
                                            {c.name}
                                        </Link>
                                        {c.archived && <span className="ml-2 chip">archived</span>}
                                    </td>
                                    <td className="p-3 hidden md:table-cell">
                                        <span className="inline-flex items-center gap-1 chip chip-doc">
                                            {c.type === "corporate" ? <Buildings size={11} /> : <UserIcon size={11} />}
                                            {c.type}
                                        </span>
                                    </td>
                                    <td className="p-3 hidden lg:table-cell text-xs text-muted-foreground">
                                        {c.phone && <div>{c.phone}</div>}
                                        {c.email && <div>{c.email}</div>}
                                    </td>
                                    <td className="p-3 hidden lg:table-cell text-xs text-muted-foreground">{c.matter_type_default || "—"}</td>
                                    <td className="p-3 text-right">
                                        <span className="chip chip-ai">{c.matters_count || 0}</span>
                                    </td>
                                    <td className="p-3">
                                        <Link to={`/clients/${c.id}`} className="text-muted-foreground hover:text-foreground">
                                            <ArrowRight size={14} />
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function IntakeForm({ onDone }) {
    const [v, setV] = useState({
        name: "",
        type: "individual",
        phone: "",
        email: "",
        address: "",
        matter_type_default: "",
        opposing_party_for_conflict_check: "",
        referral_source: "",
        notes: "",
    });
    const [conflicts, setConflicts] = useState(null);
    const [busy, setBusy] = useState(false);

    const runConflictCheck = async () => {
        if (!v.name.trim()) return;
        try {
            const r = await clientsApi.conflictCheck({
                name: v.name,
                opposing_party: v.opposing_party_for_conflict_check,
            });
            setConflicts(r.conflicts);
        } catch {
            // silent — conflict check is a nice-to-have
        }
    };

    const submit = async (force = false) => {
        if (!v.name.trim()) {
            toast.error("Client name is required");
            return;
        }
        if (!force) {
            // ensure conflict check ran
            const r = await clientsApi.conflictCheck({
                name: v.name,
                opposing_party: v.opposing_party_for_conflict_check,
            });
            setConflicts(r.conflicts);
            if (r.has_conflict) {
                return; // require user to confirm via the AlertDialog below
            }
        }
        setBusy(true);
        try {
            const created = await clientsApi.create({
                name: v.name.trim(),
                type: v.type,
                phone: v.phone,
                email: v.email,
                address: v.address,
                matter_type_default: v.matter_type_default,
                referral_source: v.referral_source,
                notes: v.notes,
            });
            onDone?.(created);
        } catch (e) {
            console.error(e);
            toast.error(e?.response?.data?.detail || "Failed to create client");
        }
        setBusy(false);
    };

    return (
        <div className="space-y-3" data-testid="client-intake-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Labeled label="Name">
                    <Input
                        value={v.name}
                        onChange={(e) => setV({ ...v, name: e.target.value })}
                        onBlur={runConflictCheck}
                        placeholder="Full legal name"
                        data-testid="intake-name"
                    />
                </Labeled>
                <Labeled label="Type">
                    <Select value={v.type} onValueChange={(val) => setV({ ...v, type: val })}>
                        <SelectTrigger data-testid="intake-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="individual">Individual</SelectItem>
                            <SelectItem value="corporate">Corporate</SelectItem>
                        </SelectContent>
                    </Select>
                </Labeled>
                <Labeled label="Phone">
                    <Input value={v.phone} onChange={(e) => setV({ ...v, phone: e.target.value })} placeholder="+91-…" data-testid="intake-phone" />
                </Labeled>
                <Labeled label="Email">
                    <Input value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} placeholder="name@example.com" data-testid="intake-email" />
                </Labeled>
                <Labeled label="Default matter type">
                    <Input value={v.matter_type_default} onChange={(e) => setV({ ...v, matter_type_default: e.target.value })} placeholder="e.g., Family / Criminal / Commercial" data-testid="intake-matter-type" />
                </Labeled>
                <Labeled label="Referral source">
                    <Input value={v.referral_source} onChange={(e) => setV({ ...v, referral_source: e.target.value })} placeholder="e.g., Existing client / Bar / Online" data-testid="intake-referral" />
                </Labeled>
            </div>
            <Labeled label="Address">
                <Textarea rows={2} value={v.address} onChange={(e) => setV({ ...v, address: e.target.value })} data-testid="intake-address" />
            </Labeled>
            <Labeled label="Opposing party (for conflict check, optional)">
                <Input
                    value={v.opposing_party_for_conflict_check}
                    onChange={(e) => setV({ ...v, opposing_party_for_conflict_check: e.target.value })}
                    onBlur={runConflictCheck}
                    placeholder="Will be checked against existing clients"
                    data-testid="intake-opposing"
                />
            </Labeled>
            <Labeled label="Intake notes">
                <Textarea rows={3} value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} placeholder="Brief facts, urgency, anything to flag for our records." data-testid="intake-notes" />
            </Labeled>

            {conflicts && conflicts.length > 0 && (
                <div
                    className="border border-destructive/40 bg-destructive/5 rounded-sm p-3"
                    data-testid="conflict-warning"
                >
                    <div className="flex items-center gap-2 mb-2 text-destructive font-medium">
                        <WarningOctagon size={16} weight="fill" />
                        Potential conflict of interest detected
                    </div>
                    <ul className="list-disc pl-5 text-sm space-y-1">
                        {conflicts.map((c, i) => (
                            <li key={i}><strong>{c.kind.replace(/_/g, " ")}:</strong> {c.message}</li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-background border-t border-border flex justify-end mt-2 gap-2">
                {conflicts && conflicts.length > 0 ? (
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button disabled={busy} variant="outline" className="border-destructive/40 text-destructive hover:bg-destructive/10" data-testid="intake-save-anyway">
                                Save anyway
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Proceed despite conflict?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    A potential conflict of interest was detected. As an advocate, you must satisfy yourself that there is no actual conflict before accepting this engagement. The system will log this override in the audit trail.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel data-testid="intake-conflict-cancel">Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => submit(true)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="intake-conflict-confirm">
                                    I have satisfied myself — proceed
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                ) : (
                    <Button onClick={() => submit(false)} disabled={busy || !v.name.trim()} data-testid="intake-submit">
                        {busy ? "Saving…" : "Save client"}
                    </Button>
                )}
            </div>
        </div>
    );
}

function Labeled({ label, children }) {
    return (
        <div className="space-y-1.5">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
            {children}
        </div>
    );
}
